import React, { useRef, useState } from 'react';
import Modal from './Modal';
import './FileUpload.css';

/**
 * Reusable file upload component with drag-and-drop support.
 *
 * This component handles the UI for file upload but delegates validation
 * and processing to the parent component via the onFileSelect callback.
 *
 * @param {Function} onFileSelect - Callback when file is selected: (file) => void or (files) => void for multi
 * @param {boolean} dragActive - Whether drag is currently active
 * @param {Function} onDragOver - Drag over handler
 * @param {Function} onDragLeave - Drag leave handler
 * @param {Function} onDrop - Drop handler
 * @param {boolean} disabled - Whether upload is disabled
 * @param {boolean} uploading - Whether file is currently uploading
 * @param {string} error - Error message to display
 * @param {Array} uploadedFiles - Array of uploaded file objects: [{id, name, extension, uploaded_at}]
 * @param {Function} onRemoveFile - Callback when remove button is clicked: (fileId) => void
 * @param {Function} onReplaceFile - Callback when replace button is clicked: (fileId) => void
 * @param {boolean} multiFile - Whether to allow multiple file uploads (default: false)
 * @param {string} uploadText - Custom upload text (optional)
 * @param {string} uploadSubtext - Custom upload subtext (optional)
 */
const FileUpload = ({
  onFileSelect,
  dragActive,
  onDragOver,
  onDragLeave,
  onDrop,
  disabled = false,
  uploading = false,
  error = null,
  uploadedFiles = [],
  onRemoveFile = null,
  onReplaceFile = null,
  multiFile = false,
  uploadText = null,
  uploadSubtext = null
}) => {
  const inputRef = useRef(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const handleClick = (e) => {
    // Only trigger file input if clicking on the dropzone itself, not on action buttons
    if (e.target.closest('.file-action-btn')) {
      return;
    }
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    if (multiFile) {
      // Multiple files mode
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        onFileSelect(files);
      }
    } else {
      // Single file mode
      const file = e.target.files?.[0];
      if (file) {
        onFileSelect(file);
      }
    }
    e.target.value = ''; // Reset input
  };

  const handleReplaceClick = (e, fileId) => {
    e.stopPropagation();
    // Trigger file input - same as clicking the dropzone
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleRemoveClick = (e, fileId) => {
    e.stopPropagation();
    setFileToDelete(fileId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (fileToDelete && onRemoveFile) {
      await onRemoveFile(fileToDelete);
      setDeleteModalOpen(false);
      setFileToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setFileToDelete(null);
  };

  const getFileIcon = (extension) => {
    switch (extension) {
      case '.csv':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="file-icon" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM3.517 14.841a1.13 1.13 0 0 0 .401.823q.195.162.478.252.284.091.665.091.507 0 .859-.158.354-.158.539-.44.187-.284.187-.656 0-.336-.134-.56a1 1 0 0 0-.375-.357 2 2 0 0 0-.566-.21l-.621-.144a1 1 0 0 1-.404-.176.37.37 0 0 1-.144-.299q0-.234.185-.384.188-.152.512-.152.214 0 .37.068a.6.6 0 0 1 .246.181.56.56 0 0 1 .12.258h.75a1.1 1.1 0 0 0-.2-.566 1.2 1.2 0 0 0-.5-.41 1.8 1.8 0 0 0-.78-.152q-.439 0-.776.15-.337.149-.527.421-.19.273-.19.639 0 .302.122.524.124.223.352.367.228.143.539.213l.618.144q.31.073.463.193a.39.39 0 0 1 .152.326.5.5 0 0 1-.085.29.56.56 0 0 1-.255.193q-.167.07-.413.07-.175 0-.32-.04a.8.8 0 0 1-.248-.115.58.58 0 0 1-.255-.384zM.806 13.693q0-.373.102-.633a.87.87 0 0 1 .302-.399.8.8 0 0 1 .475-.137q.225 0 .398.097a.7.7 0 0 1 .272.26.85.85 0 0 1 .12.381h.765v-.072a1.33 1.33 0 0 0-.466-.964 1.4 1.4 0 0 0-.489-.272 1.8 1.8 0 0 0-.606-.097q-.534 0-.911.223-.375.222-.572.632-.195.41-.196.979v.498q0 .568.193.976.197.407.572.626.375.217.914.217.439 0 .785-.164t.55-.454a1.27 1.27 0 0 0 .226-.674v-.076h-.764a.8.8 0 0 1-.118.363.7.7 0 0 1-.272.25.9.9 0 0 1-.401.087.85.85 0 0 1-.478-.132.83.83 0 0 1-.299-.392 1.7 1.7 0 0 1-.102-.627zm8.239 2.238h-.953l-1.338-3.999h.917l.896 3.138h.038l.888-3.138h.879z"/>
          </svg>
        );
      case '.xlsx':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="file-icon" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M14 4.5V11h-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM7.86 14.841a1.13 1.13 0 0 0 .401.823q.195.162.479.252.284.091.665.091.507 0 .858-.158.355-.158.54-.44a1.17 1.17 0 0 0 .187-.656q0-.336-.135-.56a1 1 0 0 0-.375-.357 2 2 0 0 0-.565-.21l-.621-.144a1 1 0 0 1-.405-.176.37.37 0 0 1-.143-.299q0-.234.184-.384.188-.152.513-.152.214 0 .37.068a.6.6 0 0 1 .245.181.56.56 0 0 1 .12.258h.75a1.1 1.1 0 0 0-.199-.566 1.2 1.2 0 0 0-.5-.41 1.8 1.8 0 0 0-.78-.152q-.44 0-.777.15-.336.149-.527.421-.19.273-.19.639 0 .302.123.524t.351.367q.229.143.54.213l.618.144q.31.073.462.193a.39.39 0 0 1 .153.326.5.5 0 0 1-.085.29.56.56 0 0 1-.255.193q-.168.07-.413.07-.176 0-.32-.04a.8.8 0 0 1-.249-.115.58.58 0 0 1-.255-.384zm-3.726-2.909h.893l-1.274 2.007 1.254 1.992h-.908l-.85-1.415h-.035l-.853 1.415H1.5l1.24-2.016-1.228-1.983h.931l.832 1.438h.036zm1.923 3.325h1.697v.674H5.266v-3.999h.791zm7.636-3.325h.893l-1.274 2.007 1.254 1.992h-.908l-.85-1.415h-.035l-.853 1.415h-.861l1.24-2.016-1.228-1.983h.931l.832 1.438h.036z"/>
          </svg>
        );
      case '.xls':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="file-icon" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM6.472 15.29a1.2 1.2 0 0 1-.111-.449h.765a.58.58 0 0 0 .254.384q.106.073.25.114.143.041.319.041.246 0 .413-.07a.56.56 0 0 0 .255-.193.5.5 0 0 0 .085-.29.39.39 0 0 0-.153-.326q-.152-.12-.462-.193l-.619-.143a1.7 1.7 0 0 1-.539-.214 1 1 0 0 1-.351-.367 1.1 1.1 0 0 1-.123-.524q0-.366.19-.639.19-.272.527-.422.338-.15.777-.149.457 0 .78.152.324.153.5.41.18.255.2.566h-.75a.56.56 0 0 0-.12-.258.6.6 0 0 0-.247-.181.9.9 0 0 0-.369-.068q-.325 0-.513.152a.47.47 0 0 0-.184.384q0 .18.143.3a1 1 0 0 0 .405.175l.62.143q.326.075.566.211a1 1 0 0 1 .375.358q.135.222.135.56 0 .37-.188.656a1.2 1.2 0 0 1-.539.439q-.351.158-.858.158-.381 0-.665-.09a1.4 1.4 0 0 1-.478-.252 1.1 1.1 0 0 1-.29-.375m-2.945-3.358h-.893L1.81 13.37h-.036l-.832-1.438h-.93l1.227 1.983L0 15.931h.861l.853-1.415h.035l.85 1.415h.908L2.253 13.94zm2.727 3.325H4.557v-3.325h-.79v4h2.487z"/>
          </svg>
        );
      case '.json':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="file-icon" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5z"/>
            <path d="M4.151 15.29a1.2 1.2 0 0 1-.111-.449h.764a.58.58 0 0 0 .255.384q.105.073.25.114.142.041.319.041.245 0 .412-.07a.56.56 0 0 0 .255-.193.5.5 0 0 0 .085-.29.39.39 0 0 0-.153-.326q-.151-.12-.462-.193l-.618-.143a1.7 1.7 0 0 1-.539-.214 1 1 0 0 1-.352-.367 1.1 1.1 0 0 1-.123-.524q0-.366.19-.639.192-.272.528-.422.337-.15.776-.149.456 0 .78.152.325.153.5.41.18.255.2.566h-.75a.56.56 0 0 0-.12-.258.6.6 0 0 0-.247-.181.9.9 0 0 0-.369-.068q-.325 0-.513.152a.47.47 0 0 0-.184.384q0 .18.143.3a1 1 0 0 0 .405.175l.62.143q.326.075.566.211a1 1 0 0 1 .375.358q.135.222.135.56 0 .37-.188.656a1.2 1.2 0 0 1-.539.439q-.351.158-.858.158-.381 0-.665-.09a1.4 1.4 0 0 1-.478-.252 1.1 1.1 0 0 1-.29-.376m-3.104-.033v-2.49h-.8v2.45q0 .58.297.894.298.313.842.313.508 0 .8-.306.295-.31.295-.872v-2.479h-.8v2.426q0 .283-.123.418-.122.135-.353.135-.232 0-.354-.135-.123-.135-.123-.354Z"/>
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="file-icon" viewBox="0 0 16 16">
            <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5z"/>
          </svg>
        );
    }
  };

  return (
    <>
      <div
        className={`upload-dropzone ${dragActive ? 'is-dragover' : ''} ${uploadedFiles.length > 0 ? 'has-files' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleClick}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xls,.xlsx,.json"
          onChange={handleFileChange}
          hidden
          disabled={disabled}
          multiple={multiFile}
        />

        {uploadedFiles.length > 0 && (
          <div className="uploaded-files-display">
            {uploadedFiles.map((file) => (
              <div key={file.id} className="uploaded-file-item">
                <div className="file-icon-container">
                  {getFileIcon(file.extension)}
                  {onReplaceFile && (
                    <button
                      type="button"
                      className={`file-action-btn replace-btn ${!onRemoveFile ? 'replace-only' : ''}`}
                      onClick={(e) => handleReplaceClick(e, file.id)}
                      title="Replace file"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9"/>
                        <path fillRule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z"/>
                      </svg>
                    </button>
                  )}
                  {onRemoveFile && (
                    <button
                      type="button"
                      className="file-action-btn remove-btn"
                      onClick={(e) => handleRemoveClick(e, file.id)}
                      title="Remove file"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
                      </svg>
                    </button>
                  )}
                </div>
                <p className="file-name">{file.name}</p>
              </div>
            ))}
          </div>
        )}

        <p className="upload-text">
          {uploadText || (multiFile 
            ? <>Drag and drop your files here, or <span className="upload-link">upload</span></>
            : <>Drag and drop your dataset here, or <span className="upload-link">upload</span></>
          )}
        </p>
        <p className="upload-subtext">
          {uploadSubtext || (multiFile
            ? 'Supported formats: .csv, .xls, .xlsx, .json — Max 200MB each — Multiple files allowed'
            : 'Supported formats: .csv, .xls, .xlsx, .json — Max 200MB'
          )}
        </p>
      </div>

      {error && <div className="upload-error">{error}</div>}

      {uploading && (
        <div className="uploading">Uploading...</div>
      )}

      <Modal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Dataset"
        message="Are you sure you want to delete this dataset? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        informationLevel="error"
      />
    </>
  );
};

export default FileUpload;

